// MobileBottomBar.jsx — iOS/Android-style bottom nav for mobile.
// v11 (2026-02-17) — Iter41:
//   - Uses --z-bottombar (see index.css) — no literal z-index in JSX.
//   - The 🔔 notif button respects Notification.permission:
//       "granted"/"default" → tap opens the notif drawer.
//       "denied"            → button is disabled and reads "الإشعارات مرفوضة".
import React, { useCallback, useEffect, useState } from "react";
import { Home, Calculator, Coffee, Bell, BellOff, Menu } from "lucide-react";
import { api } from "./lib/api";
import { InfoTip } from "./components/InfoTip";

const ITEMS = [
  { id: "home",   label: "الرئيسية",   Icon: Home,       tab: "home" },
  { id: "ops",    label: "العمليات",   Icon: Coffee,     tab: "op_manager" },
  { id: "pos",    label: "POS",         Icon: Calculator, tab: "pos" },
  { id: "notif",  label: "الإشعارات",  Icon: Bell,       kind: "notif" },
  { id: "menu",   label: "القائمة",    Icon: Menu,       kind: "menu" },
];

const readNotifPerm = () => {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission || "default";
};

export default function MobileBottomBar({ currentTab, onNav, onOpenNotif, onOpenMenu }) {
  const [unread, setUnread] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 769 : false
  );
  const [notifPerm, setNotifPerm] = useState(readNotifPerm());

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener("resize", onResize);
    // Re-check permission on focus (user may have flipped it in Settings).
    const onFocus = () => setNotifPerm(readNotifPerm());
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    loadCount();
    const t = setInterval(loadCount, 30_000);
    return () => clearInterval(t);
  }, [loadCount, isMobile]);

  // Hard gate: don't render at all on non-mobile widths.
  if (!isMobile) return null;

  const notifDenied = notifPerm === "denied";

  return (
    <nav dir="rtl" data-testid="mobile-bottom-bar" className="mbb">
      {ITEMS.map(({ id, label, Icon, tab, kind }) => {
        const isNotif = kind === "notif";
        const active = tab && currentTab === tab;
        const disabled = isNotif && notifDenied;
        const NotifIcon = notifDenied ? BellOff : Icon;
        const displayIcon = isNotif ? NotifIcon : Icon;
        const displayLabel = isNotif && notifDenied ? "الإشعارات مرفوضة" : label;
        const title = isNotif && notifDenied
          ? "الإشعارات مرفوضة — فعّلها من إعدادات الجهاز ← الإشعارات ← ثواني"
          : undefined;
        const handle = () => {
          if (disabled) return;
          if (kind === "notif") onOpenNotif?.();
          else if (kind === "menu") onOpenMenu?.();
          else if (tab) onNav?.(tab);
        };
        return (
          <button
            key={id}
            type="button"
            onClick={handle}
            disabled={disabled}
            aria-disabled={disabled || undefined}
 
            data-testid={`mbb-${id}`}
            data-notif-state={isNotif ? notifPerm : undefined}
            className={`mbb-item${active ? " is-active" : ""}${disabled ? " is-disabled" : ""}`}
           aria-label={title}>
            <div style={{ position: "relative" }}>
              {React.createElement(displayIcon, { size: 22, strokeWidth: active ? 2.5 : 2 })}
              {id === "notif" && !notifDenied && unread > 0 && (
                <span className="mbb-badge" data-testid={`mbb-badge-${id}`}>
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </div>
            <span className="mbb-label">{displayLabel}</span>
            {active && <span className="mbb-underline" aria-hidden />}
          </button>
        );
      })}
    </nav>
  );
}
